import { gsap } from 'gsap'
import { createElement } from '@/_utilities/dom'
import { buildListSections } from '@/ui/list-view-helpers'
import { celestialBodies } from '@/_constants/bodies'
import type { CelestialBody } from '@/_types/celestial-body'

/**
 * A scrollable, over-the-top directory of every world, layered over the live 3D
 * scene with a blurred backdrop so the nebula still glows through. Rows are
 * accent-lit cards with a pulsing mini-orb; they stagger in when opened. This is
 * the easy-to-navigate counterpart to the cosmos view, without being any calmer.
 */
export class ListView {
  public readonly element: HTMLDivElement

  private readonly rows: HTMLElement[]
  private readonly onEnter: (body: CelestialBody) => void
  private readonly onClose: () => void
  private isOpen: boolean

  /**
   * @param {(body: CelestialBody) => void} onEnter - Called to enter a world.
   * @param {() => void} onClose - Called to return to the cosmos view.
   */
  public constructor(onEnter: (body: CelestialBody) => void, onClose: () => void) {
    this.onEnter = onEnter
    this.onClose = onClose
    this.rows = []
    this.isOpen = false

    this.element = createElement('div', {
      className: 'list-view',
      attributes: { 'aria-hidden': 'true' },
    })
    const inner = createElement('div', { className: 'list-view-inner' })
    inner.append(this.buildHeader(), ...this.buildSections())
    this.element.append(inner)

    this.handleKeyDown = this.handleKeyDown.bind(this)
  }

  /**
   * Open the list view and stagger its rows in.
   *
   * @returns {void}
   */
  public show(): void {
    this.isOpen = true
    this.element.classList.add('is-open')
    this.element.setAttribute('aria-hidden', 'false')
    this.element.scrollTop = 0
    window.addEventListener('keydown', this.handleKeyDown)

    gsap.fromTo(
      this.rows,
      { opacity: 0, y: 28 },
      { opacity: 1, y: 0, duration: 0.5, stagger: 0.035, ease: 'power3.out' },
    )
  }

  /**
   * Close the list view.
   *
   * @returns {void}
   */
  public hide(): void {
    this.isOpen = false
    this.element.classList.remove('is-open')
    this.element.setAttribute('aria-hidden', 'true')
    window.removeEventListener('keydown', this.handleKeyDown)
  }

  /**
   * Whether the list view is currently open.
   *
   * @returns {boolean} True when open.
   */
  public getIsOpen(): boolean {
    return this.isOpen
  }

  /**
   * Build the list header with the title and a back-to-cosmos control.
   *
   * @returns {HTMLDivElement} The header element.
   */
  private buildHeader(): HTMLDivElement {
    const header = createElement('div', { className: 'list-view-header' })

    const titleBlock = createElement('div', { className: 'list-view-title-block' })
    titleBlock.append(
      createElement('h1', { className: 'list-view-title', text: 'Everything QuickCasa' }),
      createElement('p', { className: 'list-view-subtitle', text: 'Every product, one tap away.' }),
    )

    const backButton = createElement('button', {
      className: 'list-view-back',
      html: '<span aria-hidden="true">←</span> Galaxy view',
    })
    backButton.addEventListener('click', () => {
      this.onClose()
    })

    header.append(titleBlock, backButton)
    return header
  }

  /**
   * Build every tier section and its rows.
   *
   * @returns {HTMLElement[]} The section elements.
   */
  private buildSections(): HTMLElement[] {
    const sections = buildListSections(celestialBodies)
    return sections.map((section) => {
      const sectionElement = createElement('section', { className: 'list-section' })

      const heading = createElement('div', { className: 'list-section-heading' })
      heading.append(
        createElement('h2', { className: 'list-section-label', text: section.label }),
        createElement('span', { className: 'list-section-caption', text: section.caption }),
      )
      sectionElement.append(heading)

      for (const body of section.bodies) {
        sectionElement.append(this.buildRow(body))
      }
      return sectionElement
    })
  }

  /**
   * Build a single accent-lit body row.
   *
   * @param {CelestialBody} body - The body to render.
   * @returns {HTMLDivElement} The row element.
   */
  private buildRow(body: CelestialBody): HTMLDivElement {
    const row = createElement('div', { className: 'list-row' })
    row.style.setProperty('--row-accent', body.accentColour)
    row.style.setProperty('--row-glow', body.glowColour)
    row.style.setProperty('--row-surface', body.surfaceColour)

    const orb = createElement('div', { className: 'list-orb' })

    const details = createElement('div', { className: 'list-row-details' })
    const heading = createElement('div', { className: 'list-row-heading' })
    heading.append(
      createElement('h3', { className: 'list-row-name', text: body.name }),
      createElement('span', { className: 'list-row-domain', text: body.domain }),
    )
    details.append(heading, createElement('p', { className: 'list-row-description', text: body.description }))

    const enterButton = createElement('button', {
      className: 'list-row-enter',
      html: 'Open <span aria-hidden="true">→</span>',
      attributes: { 'aria-label': `Open ${body.name}` },
    })
    enterButton.addEventListener('click', () => {
      this.onEnter(body)
    })

    row.append(orb, details, enterButton)
    this.rows.push(row)
    return row
  }

  /**
   * Close the list view when Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keydown event.
   * @returns {void}
   */
  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.onClose()
    }
  }
}
